import camelCase from 'lodash/camelCase';
import { matchPath } from 'react-router-dom';
import { IBreadcrumbItem } from 'dash-ui';

import { RoutesPath } from 'flow-components/routes/routes';
import {
  IBreadcrumbScopeBuildParams,
  IBreadcrumbScopeBuildHookParams,
} from 'common/hooks/use-breadcrumb-builder/breadcrumbs-scope-builders/';
import {
  useGetMediaFolderByIdLazyQuery,
  useGetPropertyByIdLazyQuery,
} from 'gql/generated.graphql';
import {
  DEFAULT_PROPERTY_TAB_KEY,
  PropertyTabsKeys,
} from 'pages/properties/PropertyDetailsPage/components/PropertyDetailsTabs/PropertyDetailsTabs.constants';

export const usePropertiesBreadcrumbBuilder = (): [
  IBreadcrumbScopeBuildHookParams
] => {
  const [getPropertyById] = useGetPropertyByIdLazyQuery();
  const [getFolderById] = useGetMediaFolderByIdLazyQuery();

  return [
    async ({ pathname, t, currentTab }: IBreadcrumbScopeBuildParams) => {
      const items: IBreadcrumbItem[] = [];

      // set home breadcrumb
      items.push({
        label: t`properties`,
        path: RoutesPath.Properties,
        icon: 'home2',
      });

      // get any matched property details path
      const propertiesParams: any =
        matchPath(RoutesPath.PropertyDetails, pathname) ||
        matchPath(RoutesPath.PropertyMediaFolder, pathname) ||
        matchPath(RoutesPath.PropertyPreview, pathname);

      const propertyId = propertiesParams?.params?.id;
      const folderId = propertiesParams?.params?.folderId;

      if (propertyId) {
        const propertyPath = RoutesPath.PropertyDetails.replace(
          ':id',
          propertyId
        );
        const { data } = await getPropertyById({ variables: { propertyId } });
        const propertyName = data?.getPropertyById?.name;

        // add property name as breadcrumb
        if (propertyName) {
          items.push({ label: propertyName, path: propertyPath });
        }

        if (currentTab && currentTab !== DEFAULT_PROPERTY_TAB_KEY) {
          // add property tab as breadcrumb
          items.push({
            label: t(camelCase(currentTab)),
            path: `${propertyPath}?tab=${currentTab}`,
          });
        }

        if (folderId) {
          const getMediaFolderPath = (folderId: string): string =>
            RoutesPath.PropertyMediaFolder.replace(':id', propertyId).replace(
              ':folderId',
              folderId
            );
          // specify for media a media tab as breadcrumb
          items.push({
            label: t(camelCase(PropertyTabsKeys.MEDIA)),
            path: `${propertyPath}?tab=${PropertyTabsKeys.MEDIA}`,
          });

          const { data } = await getFolderById({ variables: { folderId } });
          const { folderName, foldersPathCollection = [] } =
            data?.getMediaFolderById || {};

          // set folders parents as breadcrumbs
          foldersPathCollection?.forEach((parent) => {
            items.push({
              label: parent.folderName,
              path: getMediaFolderPath(parent._id),
            });
          });

          // set active folder as last item in the breadcrumb chain
          if (folderName) {
            items.push({
              label: folderName,
              path: getMediaFolderPath(folderId),
            });
          }
        }

        // set preview breadcrumb
        if (propertiesParams.pattern.path === RoutesPath.PropertyPreview) {
          items.push({
            label: t`preview`,
            path: RoutesPath.PropertyPreview.replace(':id', propertyId),
          });
        }
      }

      return items;
    },
  ];
};
